import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Pagination,
  Box,
  Grid,
  Switch,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import NavigationTab from "../navigation";
import { Cluster_flashing } from "./cluster_interface";
import FirmwareUpdateDialog from "./firmwareupdatedialog";
import CloseIcon from "@mui/icons-material/Close";
import {Transition, Transitioncon} from "../assets/assets"

const StyledTableCell = styled(TableCell)(() => ({
  fontWeight: "bold",
  color: "white",
}));

const formatVersion = (build: number, major: number, minor: number) => {
  return `${build}.${major}.${minor}`;
};

const Cluster: React.FC = () => {
  const [clusterFlashing, setClusterFlashing] = useState<Cluster_flashing[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState<Cluster_flashing | null>(null);
  const [newVersion, setNewVersion] = useState({ build: 0, major: 0, minor: 0, status: false });
  const [firmwareStatuses, setFirmwareStatuses] = useState<{ [firmwareId: number]: boolean }>({});
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [clusterToToggle, setClusterToToggle] = useState<Cluster_flashing | null>(null);
  const [pendingStatus, setPendingStatus] = useState<boolean>(false);

  const fetchApiStatus = async (firmwareId: number): Promise<boolean> => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/firmware/getOne/${firmwareId}`);
      const data = await res.json();
      return data.status;
    } catch (err) {
      console.error(`Error fetching status for firmware ${firmwareId}:`, err);
      return false;
    }
  };

  const fetchCluster = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cluster/getAll`);
      const data = await res.json();

      const filteredData = data.filter((cluster: Cluster_flashing) =>
        ['PH1', 'PH2', 'COLOR'].includes(cluster.clusterTypee)
      );

      setClusterFlashing(filteredData);
      setTotalCount(filteredData.length);

      const statuses: { [firmwareId: number]: boolean } = {};
      await Promise.all(
        filteredData.map(async (cluster: Cluster_flashing) => {
          const firmwareId = cluster.firmware.id;
          const status = await fetchApiStatus(firmwareId);
          statuses[firmwareId] = status;
        })
      );
      setFirmwareStatuses(statuses);
    } catch (err) {
      console.error("Error fetching clusters:", err);
    }
  };

  useEffect(() => {
    fetchCluster();
  }, [page]);

  const handleEditUser = (cluster: Cluster_flashing) => {
    setSelectedCluster(cluster);
    setNewVersion({
      build: cluster.firmware.build,
      major: cluster.firmware.major,
      minor: cluster.firmware.minor,
      status: cluster.firmware.status,
    });
    setEditDialogOpen(true);
  };

  const handleFileUpload = async (file: File) => {
    if (!selectedCluster?.id) {
      throw new Error("No cluster selected");
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/firmware/uploadFile/${selectedCluster.id}`,
        {
          method: "PUT",
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Upload failed");
      }

      const result = await response.json();
      console.log("Upload successful:", result);
      fetchCluster();
      return result;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const handleVersionUpdate = (build: number | null, major: number, minor: number, status: boolean) => {
    if (!selectedCluster) return;

    const payload = {
      major,
      minor,
      status,
      ...(build !== null && { build }),
    };

    fetch(`${process.env.REACT_APP_API_BASE_URL}/firmware/${selectedCluster.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then(() => {
        fetchCluster();
      })
      .catch((error) => console.error("Error updating version:", error));
  };

  const openStatusDialog = (cluster: Cluster_flashing, newStatus: boolean) => {
    setClusterToToggle(cluster);
    setPendingStatus(newStatus);
    setStatusDialogOpen(true);
  };

  const handleStatusChangeConfirm = async () => {
    if (!clusterToToggle) return;

    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/firmware/${clusterToToggle.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: pendingStatus }),
      });

      setFirmwareStatuses((prev) => ({
        ...prev,
        [clusterToToggle.firmware.id]: pendingStatus,
      }));
      setStatusDialogOpen(false);
    } catch (err) {
      console.error("Failed to update firmware status:", err);
    }
  };

  return (
    <Box sx={{ background: "#EEEEEE", paddingBottom: 8 }}>
      <NavigationTab />
      <Grid container sx={{ marginTop: 8 }}>
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            background: "transparent",
          }}
        >
          <Box
            sx={{
              width: "1200px",
              background: "white",
              border: "0.5px solid red",
              borderRadius: "18px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "10px",
              }}
            >
              <Typography variant="h6" sx={{ color: "black" }}>
                Cluster Firmware List
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "red" }}>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Flash?</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Name</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Cluster Type</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Version</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>ACTION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clusterFlashing.map((cluster, index) => (
                    <TableRow key={index}
                      sx={{ backgroundColor: index % 2 === 0 ? '#f0f0f0' : '#ffffff' }}
                    >
                      <StyledTableCell>
                        <Switch
                          checked={!!firmwareStatuses[cluster.firmware.id]}
                          onChange={(event) => openStatusDialog(cluster, event.target.checked)}
                          color="primary"
                        />
                      </StyledTableCell>
                      <StyledTableCell sx={{ color: "black" }}>{cluster.name}</StyledTableCell>
                      <StyledTableCell sx={{ color: "black" }}>{cluster.clusterTypee}</StyledTableCell>
                      <StyledTableCell sx={{ color: "black" }}>
                        {formatVersion(
                          cluster.firmware.build,
                          cluster.firmware.major,
                          cluster.firmware.minor
                        )}
                      </StyledTableCell>
                      <StyledTableCell>
                        <IconButton onClick={() => handleEditUser(cluster)} sx={{ marginLeft: 1 }}>
                          <Edit sx={{ color: "black" }} />
                        </IconButton>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
              {/* <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "white",
                    backgroundColor: "red",
                    borderRadius: "8px",
                    margin: "0 4px",
                  },
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: "black",
                    color: "white",
                  },
                  "& .MuiPaginationItem-root:hover": {
                    backgroundColor: "green",
                  },
                }}
              /> */}
            </Box>
          </Box>
        </Grid>
      </Grid>

  
      {/* Status Change Dialog */}
      <Dialog
       TransitionComponent={Transitioncon}
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        PaperProps={{
          sx: { 
            borderRadius: 4, minWidth: 400 ,
            boxShadow: 90, 
            transition: 'transform 0.3s ease',  },
        }}
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.4)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(144deg, rgba(252, 133, 69, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 133, 69, 0.87) 100%)", color: "white",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
          }}
        >
          <DialogTitle sx={{ m: 0, p: 0, fontWeight: "bold", fontSize: "1.25rem" }}>
            Change Firmware Status
          </DialogTitle>
          <IconButton
            onClick={() => setStatusDialogOpen(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 3, pt: 3 }}>
          <Typography variant="body1">
            Are you sure you want to{" "}
            <strong style={{ color: pendingStatus ? "green" : "red" }}>
              {pendingStatus ? "Activate" : "Deactivate"}
            </strong>{" "}
            the cluster <strong>{clusterToToggle?.name}</strong>?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
          <Button
            sx={{
              background: "linear-gradient(144deg, rgba(252, 133, 69, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 133, 69, 0.87) 100%)", color: "white",
            }}
            variant="contained"
            color={pendingStatus ? "success" : "error"}
            onClick={handleStatusChangeConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>



      {/* Firmware Edit Dialog */}
      <FirmwareUpdateDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        onUpdate={handleVersionUpdate}
        onFileUpload={handleFileUpload}
        initialVersion={newVersion}
      />
    </Box>
  );
};

export default Cluster;